import { useMemo } from 'react'
import { Collapse, Space, Tag } from 'antd'
import { CalendarOutlined, InboxOutlined } from '@ant-design/icons'
import type { TFunction } from 'i18next'

import type { SalesHistoryDateGroupRoot } from '@repo/sdk'

import type { FoStatusConfigMap } from './OrderListColumns'
import { BuyerLink } from '../../components/BuyerLink'
import { useAutoExpandedKeys } from '../../shared/hooks/useAutoExpandedKeys'
import { FoTable } from './FoTable'
import {
  formatParcelBroadcastTitles,
  groupDateGroupsByParcel,
  type FulfillmentOrderParcel,
} from './groupDateGroupsByParcel'

interface DateGroupCollapseProps {
  dateGroups: readonly SalesHistoryDateGroupRoot[]
  onViewDetail: (id: string) => void
  t: TFunction
  statusConfig: FoStatusConfigMap
}

function ParcelCollapse({
  parcels,
  onViewDetail,
  t,
  statusConfig,
}: {
  parcels: readonly FulfillmentOrderParcel[]
  onViewDetail: (id: string) => void
  t: TFunction
  statusConfig: FoStatusConfigMap
}) {
  const items = useMemo(
    () =>
      parcels.map((parcel) => {
        const anchor = parcel.fulfillmentOrders[0]
        return {
          key: parcel.parcelKey,
          label: (
            <Space>
              <InboxOutlined />
              {anchor ? <BuyerLink buyer={anchor.buyer} /> : null}
              <span>
                {formatParcelBroadcastTitles(parcel) ?? t('pages.orders.marketplaceGroupLabel')}
              </span>
              <Tag>{t('pages.orders.foCount', { count: parcel.fulfillmentOrders.length })}</Tag>
            </Space>
          ),
          children: (
            <FoTable
              fulfillmentOrders={parcel.fulfillmentOrders}
              onViewDetail={onViewDetail}
              t={t}
              statusConfig={statusConfig}
            />
          ),
        }
      }),
    [parcels, onViewDetail, t, statusConfig],
  )

  const keys = useMemo(() => items.map((item) => item.key), [items])
  const [activeKeys, handleChange] = useAutoExpandedKeys(keys)

  return <Collapse items={items} activeKey={activeKeys} onChange={handleChange} ghost />
}

/**
 * Date → parcel (택배 1개) → fulfillment orders (#76). Replaces the former date → broadcast grouping,
 * which split one parcel into a row group per broadcast.
 */
export function DateGroupCollapse({
  dateGroups,
  onViewDetail,
  t,
  statusConfig,
}: DateGroupCollapseProps) {
  const parcelDateGroups = useMemo(() => groupDateGroupsByParcel(dateGroups), [dateGroups])

  const items = useMemo(
    () =>
      parcelDateGroups.map((group) => ({
        key: group.date,
        label: (
          <Space>
            <CalendarOutlined />
            <strong>{t('pages.orders.dateGroupLabel', { date: group.date })}</strong>
            <Tag color="blue">
              {t('pages.orders.foCount', { count: group.fulfillmentOrderCount })}
            </Tag>
          </Space>
        ),
        children: (
          <ParcelCollapse
            parcels={group.parcels}
            onViewDetail={onViewDetail}
            t={t}
            statusConfig={statusConfig}
          />
        ),
      })),
    [parcelDateGroups, onViewDetail, t, statusConfig],
  )

  const keys = useMemo(() => items.map((item) => item.key), [items])
  const [activeKeys, handleChange] = useAutoExpandedKeys(keys)

  return <Collapse items={items} activeKey={activeKeys} onChange={handleChange} bordered={false} />
}
